import type { Season } from 'entities/seasons'
import type { Sport, SportTypesValue } from 'entities/sports'
import { type DataWithMeta, formatDate, getTranslatedStatus } from 'shared/lib'
import type { Competition } from '../types'
import { participantTypeMapping } from '../consts'

// Helper function to ensure RFC 3339 compliance
export const toRFC3339 = (date: Date): string => {
    return date.toISOString()
}

export const transformCompetitionItem = (response: Competition) => {
    return {
        id: response.id,
        name: response.name,
        sex: response.sex,
        generalApplicationStartDate: response.generalApplicationStartDate
            ? formatDate(response.generalApplicationStartDate, 'hr-HR')
            : '',

        generalApplicationEndDate: response.generalApplicationEndDate
            ? formatDate(response.generalApplicationEndDate, 'hr-HR')
            : '',
        individualApplicationStartDate: response.individualApplicationStartDate
            ? formatDate(response.individualApplicationStartDate, 'hr-HR')
            : '',
        individualApplicationEndDate: response.individualApplicationEndDate
            ? formatDate(response.individualApplicationEndDate, 'hr-HR')
            : '',
        competitionStartDate: response.competitionStartDate
            ? formatDate(response.competitionStartDate, 'hr-HR')
            : '',
        competitionEndDate: response.competitionEndDate
            ? formatDate(response.competitionEndDate, 'hr-HR')
            : '',
        isGeneralApplicationEndDateLocked:
            response.isGeneralApplicationEndDateLocked,
        isIndividualApplicationEndDateLocked:
            response.isIndividualApplicationEndDateLocked,
        isCompetitionEndDateLocked: response.isCompetitionEndDateLocked,
        location: response.location,
        locationRemark: response.locationRemark,
        sequenceName: response.sequenceName,
        sequenceNumber: response.sequenceNumber,
        tableStandings: response.tableStandings,
        participantTypes: response.participantTypes,
        numMaxDisciplineCompetitors: response.numMaxDisciplineCompetitors,
        statistics: response.statistics,
        sportId: response.sportId,
        seasonId: response.seasonId,
        leagueId: response.leagueId,
        organizationId: response.organizationId,
        organization: response.organization,
        createdAt: response.createdAt
            ? formatDate(response.createdAt, 'hr-HR')
            : '',
        updatedAt: response.updatedAt
            ? formatDate(response.updatedAt, 'hr-HR')
            : '',
        season: response.season && resToSeason(response.season),
        sport: response.sport && resToSport(response.sport),
        status: getTranslatedStatus.competition(response.status),
    }
}

export const resToCompetitions = (response: DataWithMeta<Competition>) => {
    return {
        items: response.items.map(transformCompetitionItem),
        meta: {
            ...response.meta,
        },
    }
}

export const resToSeason = (response: Season): Season => {
    return {
        id: response.id,
        name: response.name,
        status: response.status,
        dateStart: response.dateStart,
        dateEnd: response.dateEnd,
    }
}

export const resToSport = (response: Sport): Sport => {
    return {
        id: response.id,
        name: response.name,
        hasSets: response.hasSets,
        hasGames: response.hasGames,
        hasExtensions: response.hasExtensions,
        hasPenalties: response.hasPenalties,
        hasCaptain: response.hasCaptain,
        hasJerseys: response.hasJerseys,
        numberOfPeriods: response.numberOfPeriods,
        periodsName: response.periodsName,
        statistics: response.statistics,
        status: response.status,
        pointsAwardSystem: response.pointsAwardSystem,
        pointsForWin: response.pointsForWin,
        pointsForDraw: response.pointsForDraw,
        pointsForLoss: response.pointsForLoss,
        type: response.type.map(
            (type: string) => participantTypeMapping[type] || type
        ) as SportTypesValue[],
        fieldImageKey: response.fieldImageKey,
        createdAt: response.createdAt,
        updatedAt: response.updatedAt,
        translations: response.translations,
        sportEvents: response.sportEvents,
        groupTableColumns: response.groupTableColumns,
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformToReq = (data: Competition | Record<string, any>) => {
    if (!data) {
        return
    }
    const disciplineIds = data?.disciplineIds as
        | Array<{ value: string } | string>
        | undefined
    const shouldOmitParticipantTypes = disciplineIds && disciplineIds.length > 0
    const sportId = data.sportId as string | { value: string } | null
    const seasonId = data.seasonId as string | { value: string } | null
    const organizationId = data.organizationId as
        | string
        | { value: string }
        | null
    const leagueId = data.leagueId as string | { value: string } | null
    const tableStandings = data.tableStandings as
        | string
        | { value: string }
        | null
    const participantTypes = data.participantTypes as
        | Array<{ value: string } | string>
        | undefined
    const statistics = data.statistics as
        | Array<{ value: string } | string>
        | undefined
    const sex = data.sex as Array<{ value: string } | string> | undefined
    const sponsors = data.sponsors as
        | Array<{ id: string; type: string }>
        | undefined

    const getVal = (
        v: string | { value: string } | null | undefined
    ): string | null => {
        if (!v) return null
        return typeof v === 'object' && 'value' in v ? v.value : v
    }

    return {
        name: data.name ? data.name : null,
        generalApplicationStartDate: data.generalApplicationStartDate
            ? data.generalApplicationStartDate
            : null,
        generalApplicationEndDate: data.generalApplicationEndDate
            ? data.generalApplicationEndDate
            : null,
        individualApplicationStartDate: data.individualApplicationStartDate
            ? data.individualApplicationStartDate
            : null,
        individualApplicationEndDate: data.individualApplicationEndDate
            ? data.individualApplicationEndDate
            : null,
        competitionStartDate: data.competitionStartDate
            ? data.competitionStartDate
            : null,
        competitionEndDate: data.competitionEndDate
            ? data.competitionEndDate
            : null,
        isGeneralApplicationEndDateLocked:
            data.isGeneralApplicationEndDateLocked
                ? data.isGeneralApplicationEndDateLocked
                : false,
        isIndividualApplicationEndDateLocked:
            data.isIndividualApplicationEndDateLocked
                ? data.isIndividualApplicationEndDateLocked
                : false,
        isCompetitionEndDateLocked: data.isCompetitionEndDateLocked
            ? data.isCompetitionEndDateLocked
            : false,
        numMaxDisciplineCompetitors: disciplineIds?.length
            ? data.numMaxDisciplineCompetitors || 1
            : null,

        location: data.location ? data.location : null,
        locationRemark: data.locationRemark ? data.locationRemark : null,
        sequenceName: data.sequenceName ? data.sequenceName : null,
        sportId: getVal(sportId),
        seasonId: getVal(seasonId),
        organizationId: getVal(organizationId),
        leagueId: getVal(leagueId),
        tableStandings: getVal(tableStandings),
        participantTypes:
            !shouldOmitParticipantTypes && Array.isArray(participantTypes)
                ? participantTypes.map((v) =>
                      typeof v === 'object' && v !== null ? v.value : v
                  )
                : [],
        statistics: statistics
            ? statistics.map((statistic) =>
                  typeof statistic === 'object' ? statistic.value : statistic
              )
            : [],
        sex: sex ? sex.map((s) => (typeof s === 'object' ? s.value : s)) : [],
        disciplineIds: disciplineIds
            ? disciplineIds.map((d) => (typeof d === 'object' ? d.value : d))
            : [],
        sponsors: sponsors
            ? sponsors.map((s) => ({
                  sponsorId: s.id,
                  type: s.type,
              }))
            : [],
        sequenceNumber: data.sequenceNumber && Number(data.sequenceNumber),
    }
}

export const transformToRequestDate = (
    date: Date | string | null | undefined
): string | undefined => {
    if (!date) return undefined

    // If it's a Date object, ensure RFC 3339 format
    if (date instanceof Date) {
        return toRFC3339(date)
    }

    // If it's already an ISO string with time (RFC 3339 format)
    if (typeof date === 'string' && date.includes('T')) {
        try {
            // Validate it's a proper date with time
            const parsed = new Date(date)
            if (!isNaN(parsed.getTime())) {
                // Make sure it's in proper RFC 3339 format
                return toRFC3339(parsed)
            }
        } catch (e) {
            console.error('Invalid ISO date format:', e)
        }
    }

    // For other formats, parse and convert to RFC 3339
    try {
        // Try to parse the date string
        const parsed = new Date(date)
        if (!isNaN(parsed.getTime())) {
            return toRFC3339(parsed)
        }

        // Extract date from Croatian format
        const dateParts = date.match(/(\d{2})\.\s*(\d{2})\.\s*(\d{4})\.?/)
        if (dateParts && dateParts.length === 4) {
            const [, day, month, year] = dateParts

            // Create date with proper time (14:00)
            const parsedDate = new Date(
                Number(year),
                Number(month) - 1,
                Number(day),
                14,
                0,
                0
            )
            return toRFC3339(parsedDate)
        }
    } catch (e) {
        console.error('Error parsing date:', e)
    }

    return undefined
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformCompetitionFormUpdateToReq = (
    body: Competition | Record<string, any>
) => {
    if (!body) {
        return null
    }

    const {
        generalApplicationEndDate,
        generalApplicationStartDate,
        individualApplicationStartDate,
        individualApplicationEndDate,
        competitionStartDate,
        competitionEndDate,
        sponsors,
        participantTypes,
        disciplineIds,
        tableStandings,
        ...restBody
    } = body

    const shouldOmitParticipantTypes = disciplineIds && disciplineIds.length > 0

    // Build the cleaned request body
    const cleanedBody: Record<string, unknown> = {
        ...restBody,

        // Process each date field to ensure time is preserved
        generalApplicationStartDate: processDate(generalApplicationStartDate),
        generalApplicationEndDate: processDate(generalApplicationEndDate),
        individualApplicationStartDate: processDate(
            individualApplicationStartDate
        ),
        individualApplicationEndDate: processDate(individualApplicationEndDate),
        competitionStartDate: processDate(competitionStartDate),
        competitionEndDate: processDate(competitionEndDate),

        sponsors:
            sponsors && sponsors.length
                ? sponsors.map((sponsor: { id: string; type: string }) => ({
                      sponsorId: sponsor.id,
                      type: sponsor.type,
                  }))
                : [],
        disciplineIds:
            disciplineIds && disciplineIds.length > 0
                ? disciplineIds
                : undefined,
        participantTypes:
            !shouldOmitParticipantTypes &&
            participantTypes &&
            participantTypes.length > 0
                ? participantTypes
                : undefined,
        tableStandings: tableStandings ? tableStandings : undefined,
    }

    // Helper function to process dates consistently
    function processDate(
        date: Date | string | null | undefined
    ): string | undefined {
        if (!date) return undefined

        // If it's already a Date object
        if (date instanceof Date) {
            return toRFC3339(date)
        }

        // If it's a string in ISO format with time
        if (typeof date === 'string' && date.includes('T')) {
            try {
                const dateObj = new Date(date)
                if (!isNaN(dateObj.getTime())) {
                    return toRFC3339(dateObj) // Ensure RFC 3339 format
                }
            } catch (e) {
                console.error('Invalid date format:', e)
            }
        }

        // For other formats, try to parse and ensure RFC 3339 format
        try {
            const dateObj = new Date(date)
            if (!isNaN(dateObj.getTime())) {
                return toRFC3339(dateObj)
            }

            // Try Croatian format as a last resort
            const match = String(date).match(
                /(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})\.?/
            )
            if (match) {
                const [, day, month, year] = match
                const fixedDate = new Date(
                    Number(year),
                    Number(month) - 1,
                    Number(day),
                    14,
                    0,
                    0
                )
                return toRFC3339(fixedDate)
            }
        } catch (e) {
            console.error('Could not parse date:', e)
        }

        return undefined
    }

    // Filter out undefined values
    return Object.fromEntries(
        Object.entries(cleanedBody).filter(([, value]) => value !== undefined)
    )
}
